import { createContext, useState, useRef, useCallback } from "react";
import SeamService from "../services/SeamService";

export const SeamContext = createContext();

export const SeamProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);
  const [portalUrl, setPortalUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [error, setError] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [resSyncLoading, setResSyncLoading] = useState(false);
  const [resSyncResult, setResSyncResult] = useState(null);
  const [timesSaving, setTimesSaving] = useState(false);
  const [timesSaved, setTimesSaved] = useState(false);
  const [monitoringActivity, setMonitoringActivity] = useState([]);
  const [monitoringDevices, setMonitoringDevices] = useState([]);
  const [monitoringLoading, setMonitoringLoading] = useState(false);
  const [monitoringError, setMonitoringError] = useState(null);

  // Debounce ref for reservation sync
  const resSyncInFlight = useRef(false);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await SeamService.getSettings();
      setSettings(data);
      return data;
    } catch (err) {
      console.error("Error loading Seam settings:", err);
      setError("Failed to load smart lock settings.");
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (updates) => {
    try {
      setError(null);
      const { data } = await SeamService.updateSettings(updates);
      setSettings(data);
      return data;
    } catch (err) {
      console.error("Error updating Seam settings:", err);
      setError("Failed to update smart lock settings.");
      throw err;
    }
  };

  const createPortal = useCallback(async () => {
    try {
      setPortalLoading(true);
      setError(null);
      const { data } = await SeamService.createPortal();
      setPortalUrl(data.url);
      return data.url;
    } catch (err) {
      console.error("Error creating portal:", err);
      setError("Failed to load the smart locks management portal.");
    } finally {
      setPortalLoading(false);
    }
  }, []);

  const syncSpaces = async () => {
    try {
      setSyncLoading(true);
      setSyncResult(null);
      setError(null);
      const { data } = await SeamService.syncSpaces();
      setSyncResult(
        `Successfully synced ${data.synced} ${data.synced === 1 ? "property" : "properties"} to Seam.`
      );
      return data;
    } catch (err) {
      console.error("Error syncing spaces:", err);
      setError("Failed to sync properties. Please try again.");
    } finally {
      setSyncLoading(false);
    }
  };

  const syncReservations = async () => {
    // Debounce: skip if a sync is already in flight on the client. The
    // server also debounces by returning `already_running:true` for the
    // current run if there is already a queued/running job for the
    // account.
    if (resSyncInFlight.current) return;
    resSyncInFlight.current = true;

    const buildResultMessage = (run) => {
      const parts = [];
      if (run.synced_count > 0) {
        parts.push(
          `Synced ${run.synced_count} ${run.synced_count === 1 ? "reservation" : "reservations"} across ${run.mapped_properties_count} ${run.mapped_properties_count === 1 ? "property" : "properties"} with mapped locks.`
        );
      }
      if (run.deactivated_count > 0) {
        parts.push(
          `Deactivated codes for ${run.deactivated_count} cancelled/deleted ${run.deactivated_count === 1 ? "reservation" : "reservations"}.`
        );
      }
      if (parts.length === 0) {
        return "No reservations needed syncing.";
      }
      return parts.join(" ");
    };

    const TERMINAL = ["succeeded", "partial", "failed"];
    const POLL_INTERVAL_MS = 4000;
    const POLL_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes hard cap

    try {
      setResSyncLoading(true);
      setResSyncResult(null);
      setError(null);

      const { data: queued } = await SeamService.syncReservations();
      const syncRunId = queued.sync_run_id;

      if (queued.already_running) {
        setResSyncResult(
          "A sync is already in progress for your account — waiting for it to finish…"
        );
      } else {
        setResSyncResult("Sync queued — this may take a few minutes…");
      }

      // Poll for terminal status.
      const startedAt = Date.now();
      let finalRun = null;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
        const { data: run } = await SeamService.getSyncRunStatus(syncRunId);
        if (TERMINAL.includes(run.status)) {
          finalRun = run;
          break;
        }
        if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
          throw new Error(
            "Sync is taking longer than expected. Check back shortly."
          );
        }
      }

      if (finalRun.status === "failed") {
        setResSyncResult(null);
        setError(
          finalRun.error || "Failed to sync reservations. Please try again."
        );
        return finalRun;
      }

      let message = buildResultMessage(finalRun);
      if (finalRun.status === "partial" && finalRun.error) {
        message += ` Some operations did not complete: ${finalRun.error}`;
      }
      setResSyncResult(message);
      return finalRun;
    } catch (err) {
      console.error("Error syncing reservations:", err);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to sync reservations. Please try again."
      );
    } finally {
      setResSyncLoading(false);
      resSyncInFlight.current = false;
    }
  };

  const saveTimes = async (checkInTime, checkOutTime) => {
    try {
      setTimesSaving(true);
      setTimesSaved(false);
      setError(null);
      const { data } = await SeamService.updateSettings({
        standard_check_in_time: checkInTime,
        standard_check_out_time: checkOutTime,
      });
      setSettings(data);
      setTimesSaved(true);
      setTimeout(() => setTimesSaved(false), 3000);
      return data;
    } catch (err) {
      console.error("Error saving times:", err);
      setError("Failed to save check-in/out times.");
    } finally {
      setTimesSaving(false);
    }
  };

  const getMonitoringActivity = useCallback(async (params = {}) => {
    try {
      setMonitoringLoading(true);
      setMonitoringError(null);
      const { data } = await SeamService.getMonitoringActivity(params);
      const rows = Array.isArray(data) ? data : [];
      setMonitoringActivity(rows);
      return rows;
    } catch (err) {
      console.error("Error loading Seam monitoring activity:", err);
      setMonitoringError("Failed to load smart lock activity.");
      setMonitoringActivity([]);
      return [];
    } finally {
      setMonitoringLoading(false);
    }
  }, []);

  const getMonitoringDevices = useCallback(async (params = {}) => {
    try {
      setMonitoringLoading(true);
      setMonitoringError(null);
      const { data } = await SeamService.getMonitoringDevices(params);
      const rows = Array.isArray(data) ? data : [];
      setMonitoringDevices(rows);
      return rows;
    } catch (err) {
      console.error("Error loading Seam monitoring devices:", err);
      setMonitoringError("Failed to load smart lock devices.");
      setMonitoringDevices([]);
      return [];
    } finally {
      setMonitoringLoading(false);
    }
  }, []);

  const seamCtx = {
    settings,
    portalUrl,
    loading,
    portalLoading,
    syncLoading,
    error,
    syncResult,
    resSyncLoading,
    resSyncResult,
    timesSaving,
    timesSaved,
    monitoringActivity,
    monitoringDevices,
    monitoringLoading,
    monitoringError,
    setError,
    setPortalUrl,
    setSyncResult,
    setResSyncResult,
    loadSettings,
    updateSettings,
    createPortal,
    syncSpaces,
    syncReservations,
    saveTimes,
    getMonitoringActivity,
    getMonitoringDevices,
  };

  return (
    <SeamContext.Provider value={seamCtx}>{children}</SeamContext.Provider>
  );
};
